import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import {
    MDBAlert,
    MDBBtn,
    MDBIcon,
    MDBInput,
    MDBModal,
    MDBModalBody,
    MDBModalContent,
    MDBModalDialog,
    MDBModalHeader,
    MDBModalTitle,
    MDBRadio,
    MDBSpinner,
} from "mdb-react-ui-kit";
import { uploadIcon } from "../images";
import { useNavigate } from "react-router-dom";
import {
    saveSSOInfo,
    getSSOInfo,
    deleteSSOInfo,
} from "../../apiclients/streamsapi_bridge";
import SaveChanges from "../topviews/saveCancel";
import { useForm } from "react-hook-form";
import ConfirmAlertMS from "./confirm-alert-ms";
import TooltipMsSso from "./tooltip-ms-sso";
import SSOInformationGuide from "./sso_information_guide";
import { downloadMDoc, parseXMLDoc } from "./helper-ms";
import TabIDConstant from "../../apiclients/TabIDConstant";
import { useAdminContext } from "../adminstore/Admin-Context";

function SingleSignOnSettings({ active, resetKey, ...props }) {
    const [selectedRegion, setSelectedRegion] = useState("IN");
    const { t } = useTranslation();
    const { isEdited, setEdited, isEditedSso, setEditedSso } = useAdminContext();

    const [ssoEnable, setSsoEnable] = useState(false);
    const [basicModal, setBasicModal] = useState(false);
    const [formErrors, setFormErrors] = useState({});
    const [uploadedCertificate, setUploadedCertificate] = useState(null);
    const [isSsoToggleEdited, setIsSsoToggleEdited] = useState(false);
    const [showSSODisableModal, setShowSSODisableModal] = useState(false);
    const [showInfoGuide, setShowInfoGuide] = useState(false);
    const [loading, setLoading] = useState(false);
    const [updateSuccessMessage, setUpdateSuccessMessage] = useState("");
    const [updateFailureMessage, setUpdateFailureMessage] = useState(false);
    const [fileUploadSuccess, setFileUploadSuccess] = useState(false);
    const [fileUploadFailure, setFileUploadFailure] = useState(false);

    const navigate = useNavigate();

    const {
        register,
        control,
        formState: { isDirty, dirtyFields, errors },
        setValue,
        watch,
        reset,
        handleSubmit,
    } = useForm({
        defaultValues: {
            update_users: [],
        },
    });

    const isSave = Object.keys(dirtyFields).length === 0 ? false : true;

    // Toggle for opening and closing the SSO upload modal
    const toggleOpen = () => {
        setBasicModal(!basicModal);
    };

    const [formValues, setFormValues] = useState({
        entity_id: "",
        tenant_id: "",
        signinurl: "",
        nameIdFormat: "",
        claimTypes: "",
        certificate: "",
        loginoption: 0, // 0 = Optional, 1 = Required
        ssoaccountid: "Microsoft Entra ID",
        expirydate: "N/A",
    });
    const [previousValues, setPreviousValues] = useState({ ...formValues });

    const handleRadioChange = (value) => {
        // If clicking the same option → no change
        if (value === formValues.loginoption) {
            return;
        }

        const updated = { ...formValues, loginoption: value };
        setFormValues(updated);

        // If changed from saved value → mark edited
        if (value !== previousValues.loginoption) {
            setEditedSso(true);
            setEdited(true);
            setIsSsoToggleEdited(true);
        } else {
            // Revert back to original → clear edited status
            setEditedSso(false);
            setEdited(false);
            setIsSsoToggleEdited(false);
        }
    };


    // Validation functions (left unchanged)
    const validateEntityId = (entityId) => {
        if (!entityId || !entityId.trim()) {
            return "Entity ID is required";
        }
        const entityIdPattern = /^https:\/\/[a-zA-Z0-9.-]+\/[a-zA-Z0-9-]+\/?$/;
        if (!entityIdPattern.test(entityId.trim())) {
            return "Entity ID must be in format: https://domain.com/identifier (e.g., https://sts.windows.net/0f31fb05-88cc-46c9-9c0c-6ed829560796/)";
        }
        return null;
    };

    const validateTenantId = (tenantId) => {
        if (!tenantId || !tenantId.trim()) {
            return "Tenant ID is required";
        }
        const guidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (!guidPattern.test(tenantId.trim())) {
            return "Tenant ID must be a valid GUID format (e.g., of31fb05-88cc-46c9-9c0c-6ed829560796)";
        }
        return null;
    };

    const validateSSOEndpoint = (ssoEndpoint) => {
        if (!ssoEndpoint || !ssoEndpoint.trim()) {
            return "SSO Endpoint is required";
        }
        const ssoPattern = /^https:\/\/login\.microsoftonline\.com\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/saml2$/i;
        if (!ssoPattern.test(ssoEndpoint.trim())) {
            return "SSO Endpoint must be in format: https://login.microsoftonline.com/{tenant-id}/saml2";
        }
        return null;
    };

    const handleInputChange = (e, field) => {
        const value = e.target.value;
        setFormErrors(prevErrors => ({ ...prevErrors, [field]: "" }));

        let fieldError = null;
        switch (field) {
            case 'entity_id':
                fieldError = validateEntityId(value);
                break;
            case 'tenant_id':
                fieldError = validateTenantId(value);
                break;
            case 'signinurl':
                fieldError = validateSSOEndpoint(value);
                break;
            default:
                break;
        }
        if (fieldError) {
            setFormErrors(prevErrors => ({ ...prevErrors, [field]: fieldError }));
        }

        const updatedValues = { ...formValues, [field]: value };
        setFormValues(updatedValues);
        setEditedSso(true);
        setEdited(true);
    };

    const validateForm = () => {
        try {
            const errors = {};
            const entityIdError = validateEntityId(formValues.entity_id);
            if (entityIdError) errors.entity_id = entityIdError;
            const tenantIdError = validateTenantId(formValues.tenant_id);
            if (tenantIdError) errors.tenant_id = tenantIdError;
            const ssoEndpointError = validateSSOEndpoint(formValues.signinurl);
            if (ssoEndpointError) errors.signinurl = ssoEndpointError;
            if (!formValues.certificate?.trim()) {
                errors.certificate = "Certificate is required";
            }
            if (formValues.entity_id && formValues.tenant_id) {
                const entityIdTenantMatch = formValues.entity_id.match(/\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
                if (entityIdTenantMatch && entityIdTenantMatch[1].toLowerCase() !== formValues.tenant_id.toLowerCase()) {
                    errors.tenant_id = "Tenant ID must match the tenant ID in Entity ID";
                }
            }
            if (formValues.signinurl && formValues.tenant_id) {
                const ssoTenantMatch = formValues.signinurl.match(/\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\//i);
                if (ssoTenantMatch && ssoTenantMatch[1].toLowerCase() !== formValues.tenant_id.toLowerCase()) {
                    errors.signinurl = "Tenant ID in SSO Endpoint must match the Tenant ID field";
                }
            }
            setFormErrors(errors);
            return Object.keys(errors).length === 0;
        } catch (error) {
            console.error("Validation error:", error);
            setFormErrors({
                general: "An unexpected error occurred during validation.",
            });
            return false;
        }
    };

    // Reset form values and close modal
    const revertToPreviousValues = () => {
        if (previousValues && Object.keys(previousValues).length > 0) {
            setFormValues(previousValues);
            setSsoEnable(previousValues.isssoenabled === 1);
            if (previousValues.isssoenabled === undefined) {
                setSsoEnable(false);
            }
        } else {
            setSsoEnable(false);
            setFormValues({});
        }
        setEdited(false);
        setEditedSso(false);
        setIsSsoToggleEdited(false);
    };

    const resetForm = () => {
        setBasicModal(false);
        setFormErrors({});
        setEdited(false);
        setEditedSso(false);
        setIsSsoToggleEdited(false);
        revertToPreviousValues();
        setFormValues(previousValues);
        setSsoEnable(previousValues.isssoenabled === 1);
        if (previousValues.isssoenabled === undefined) {
            setSsoEnable(false);
        }
        fetchSsoDetails();
    };

    useEffect(() => {
        console.log("isEditedSso changed:", isEditedSso);
    }, [isEditedSso]);

    // SINGLE persistent file input reference (rendered once)
    const fileInputRef = useRef(null);

    const handleCertificateClick = () => {
        // Guard: ensure ref exists
        if (!fileInputRef.current) {
            console.warn("File input not mounted yet.");
            return;
        }
        // Reset value before opening
        fileInputRef.current.value = "";
        fileInputRef.current.click();
    };

    const handleFileUpload = async (event) => {
        try {
            const file = event.target.files[0];
            if (!file) return;

            // Clear errors
            setFormErrors({});
            console.log("Uploading file:", file.name);

            // Close modal if open
            setBasicModal(false);

            // Parse and update form
            await parseXMLDoc(event, setFormValues);

            // Success
            setEditedSso(true);
            setEdited(true);
            setIsSsoToggleEdited(true);
            setFileUploadSuccess(true);
            setTimeout(() => setFileUploadSuccess(false), 3000);

            // Reset input
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        } catch (error) {
            console.error("Failed to upload file:", error);
            setFileUploadFailure(true);
            setTimeout(() => setFileUploadFailure(false), 3000);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    };

    const submitSSOConfig = async (delflag) => {
        // Start loading
        setLoading(true);
        setUpdateSuccessMessage("");
        setUpdateFailureMessage("");

        try {
            let validationFailed = false;
            let errorMessages = [];

            // File validation
            if (
                ssoEnable &&
                (!formValues.certificate || !formValues.certificate.trim()) &&
                delflag !== 1
            ) {
                errorMessages.push(
                    "No file detected. Please upload the required file to proceed with saving your changes."
                );
                validationFailed = true;
            }

            // Form validation
            if (!validateForm()) {
                validationFailed = true;
            }

            if (validationFailed) {
                setLoading(false); // stop loader immediately for validation errors
                setUpdateFailureMessage(errorMessages.join(" "));
                setTimeout(() => setUpdateFailureMessage(""), 10000);
                return;
            }

            /* =======================================================
               DELETE SSO
            ======================================================= */
            if (!ssoEnable || delflag === 1) {
                const response = await deleteSSOInfo(formValues.id);

                // Delay loader 3 sec then show toast
                setTimeout(() => {
                    setLoading(false);

                    setTimeout(() => {
                        if (response.ok) {
                            setUpdateSuccessMessage(
                                "Microsoft Entra ID Single Sign-On deleted successfully!"
                            );
                            clearFormData();
                            setTimeout(() => setUpdateSuccessMessage(""), 5000);
                        } else {
                            setUpdateFailureMessage("Failed to delete SSO config.");
                            setTimeout(() => setUpdateFailureMessage(""), 5000);
                        }
                    }, 50); // ensure loader hides before toast
                }, 3000);

                return;
            }

            const jsonObj = {
                isssoenabled: ssoEnable ? 1 : 0,
                entity_id: formValues.entity_id,
                tenant_id: formValues.tenant_id,
                signinurl: formValues.signinurl,
                certificate: formValues.certificate,
                ssoaccountid: formValues.ssoaccountid,
                expirydate: formValues.expirydate,
                loginoption: formValues.loginoption,
                siteid: JSON.parse(sessionStorage.getItem("admin_session")).siteid,
                sitename: JSON.parse(sessionStorage.getItem("admin_session")).sitename,
            };

            const response = await saveSSOInfo(jsonObj);
            const result = await response.json();

            // SUCCESS →
            if (result?.success) {
                setTimeout(() => {
                    setLoading(false);

                    setTimeout(() => {
                        setUpdateSuccessMessage(
                            "Microsoft Entra ID Single Sign-On has been enabled successfully."
                        );

                        setPreviousValues({
                            ...formValues,
                            isssoenabled: 1
                        });

                        setTimeout(() => setUpdateSuccessMessage(""), 5000);
                    }, 50);
                }, 3000);
            }
            // FAILURE →
            else {
                setTimeout(() => {
                    setLoading(false);

                    setTimeout(() => {
                        setUpdateFailureMessage(
                            "Failed to save Microsoft Entra ID SSO configuration."
                        );
                        setTimeout(() => setUpdateFailureMessage(""), 4000);
                    }, 50);
                }, 3000);
            }

            // Refresh data after save
            if (ssoEnable && delflag !== 1) {
                await fetchSsoDetails();
            }
        } catch (error) {
            console.error("Error submitting SSO config:", error);

            setTimeout(() => {
                setLoading(false);

                setTimeout(() => {
                    setUpdateFailureMessage(
                        "An unexpected error occurred while saving your SSO configuration."
                    );
                    setTimeout(() => setUpdateFailureMessage(""), 4000);
                }, 50);
            }, 3000);
        } finally {
            setEdited(false);
            setEditedSso(false);
            setIsSsoToggleEdited(false);
        }

    };

    useEffect(() => {
        const handler = () => submitSSOConfig();
        window.addEventListener("save-sso", handler);
        return () => window.removeEventListener("save-sso", handler);
    }, []);

    useEffect(() => {
        const shouldReset = sessionStorage.getItem("reset_sso_form");
        if (shouldReset === "true") {
            revertToPreviousValues();
            sessionStorage.removeItem("reset_sso_form");
        }
    }, []);

    const fetchSsoDetails = async () => {
        try {
            const siteid = JSON.parse(
                sessionStorage.getItem("admin_session")
            ).sitename;
            const response = await getSSOInfo(siteid, TabIDConstant.SINGLE_SIGN_ON_SUBLINK);
            if (!response.ok) {
                console.error("Failed to fetch SSO data");
                return;
            }

            const result = await response.json();
            const updatedForm = {
                id: result.data.id || "",
                entity_id: result.data.entity_id || "",
                tenant_id: result.data.tenant_id || "",
                signinurl: result.data.signinurl || "",
                certificate: result.data.cerficate || "",
                ssoaccountid: result.data.ssoaccountid || "Microsoft Entra ID",
                expirydate: result.data.expirydate || "",
                loginoption: result.data.loginoption || 0,
            };
            setFormValues(updatedForm);
            setPreviousValues({
                ...updatedForm,
                isssoenabled: result.data.isssoenabled,
            });
            setUploadedCertificate(result.data.cerficate || "");
            setSsoEnable(result.data.isssoenabled === 1);

        } catch (error) {
            console.error("Error loading SSO config:", error);
        }
    };

    // Reset form errors whenever the modal is opened or closed
    useEffect(() => {
        setFormErrors({});
    }, [basicModal]);

    useEffect(() => {
        fetchSsoDetails();
    }, []);

    const handleDownload = async () => {
        await downloadMDoc();
    }

    const clearFormData = () => {
        const clearedForm = {
            id: "",
            entity_id: "",
            tenant_id: "",
            signinurl: "",
            certificate: "",
            ssoaccountid: "Microsoft Entra ID",
            expirydate: "N/A",
            loginoption: 0,
        };

        setFormValues(clearedForm);
        setPreviousValues({ ...clearedForm, isssoenabled: 0 });
        setUploadedCertificate(null);

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }

        setBasicModal(false);
        setFileUploadSuccess(false);
        setFileUploadFailure(false);
    };

    useEffect(() => {
        if (!ssoEnable && fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, [ssoEnable]);
    useEffect(() => {
        if (resetKey === undefined) return; // guard for first render if you want
        // call existing function to revert local fields and clear local edit flags
        try {
            revertToPreviousValues();
        } catch (err) {
            console.error("Failed to revert on resetKey change:", err);
        }
    }, [resetKey]);
    return (
        <React.Fragment>
            <>
                {/* Single persistent hidden input (only one in the component) */}
                <input
                    type="file"
                    accept=".xml,.cer"
                    style={{ display: "none" }}
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                />

                <div className="mx-auto" style={{ paddingRight: "10%" }}>
                    {/* Enable SSO Toggle */}
                    <div className="add-numbermain">
                        <div className="tel-numbers">
                            <span>Enable Single Sign-On</span>
                        </div>
                        <div className="    ">
                            <label className="switch">
                                <input className="form-control"
                                    type="checkbox"
                                    checked={ssoEnable}
                                    onChange={() => {
                                        if (ssoEnable) {
                                            // Disabling SSO
                                            if (previousValues.isssoenabled === 1) {
                                                setShowSSODisableModal(true);
                                            } else {
                                                setSsoEnable(false);
                                                setEdited(false);
                                                setEditedSso(false);
                                                setIsSsoToggleEdited(false);
                                            }
                                        } else {
                                            // Enabling SSO again
                                            setSsoEnable(true);
                                            setEditedSso(true);
                                            setEdited(true);
                                            setIsSsoToggleEdited(true);

                                            setFormValues(prev => ({
                                                ...prev,
                                                certificate: "",
                                                entity_id: "",
                                                tenant_id: "",
                                                signinurl: "",
                                                expirydate: "N/A",
                                            }));

                                            if (fileInputRef.current) {
                                                fileInputRef.current.value = "";
                                            }
                                        }
                                    }}
                                />
                                <span className="slider round"></span>
                            </label>
                        </div>
                    </div>

                    {ssoEnable && (
                        <>
                            {/* Upload & Download */}

                            <div className="add-numbermain">
                                <div className="tel-numbers">
                                    <span>Download Metadata File</span>
                                    <br />
                                    <span className="phrecording text-secondary fs-small">
                                        {t("MSDownloadFile")}
                                        <br />
                                        {t("MSFileUrl")}
                                    </span>
                                </div>
                                <MDBBtn className="px-4" onClick={handleDownload}>
                                    Download
                                </MDBBtn>
                            </div>
                            <div className="add-numbermain">
                                <div className="tel-numbers">
                                    Upload your Microsoft Entra ID metadata (.xml) file
                                    <br />
                                    <span className="phrecording text-secondary fs-small">
                                        Please{" "}
                                        <a
                                            href="#"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setShowInfoGuide(true);
                                            }}
                                            rel="noopener noreferrer"
                                            style={{
                                                color: "#007bff",
                                                textDecoration: "underline",
                                                cursor: "pointer",
                                            }}
                                        >
                                            Click here
                                        </a>{" "}
                                        for more information guide about SSO.
                                    </span>
                                </div>
                                <MDBBtn className="px-4" onClick={toggleOpen}>
                                    Upload
                                </MDBBtn>
                            </div>
                            <div className="routing-border pt-0"></div>

                            {/* Form Fields */}
                            <div className="add-numbermain">
                                <div className="tel-numbers">Entity ID <span className="required-asterisk">*</span>
                                </div>
                                <div className="w-30">
                                    <MDBInput
                                        value={formValues.entity_id}
                                        onChange={(e) => handleInputChange(e, "entity_id")}
                                        placeholder="https://sts.windows.net/{tenant-id}"
                                        disabled={true}
                                    />
                                    {formErrors.entity_id && (
                                        <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
                                            {formErrors.entity_id}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="add-numbermain">
                                <div className="tel-numbers">
                                    Tenant ID <span className="required-asterisk">*</span>
                                </div>
                                <div className="w-30">
                                    <MDBInput
                                        value={formValues.tenant_id}
                                        onChange={(e) => handleInputChange(e, "tenant_id")}
                                        placeholder="of31fb05-88cc-46c9-9c0c-6ed829560796"
                                        disabled={true}
                                    />
                                    {formErrors.tenant_id && (
                                        <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
                                            {formErrors.tenant_id}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="add-numbermain">
                                <div className="tel-numbers">
                                    SSO Endpoint <span className="required-asterisk">*</span>
                                </div>
                                <div className="w-30">
                                    <MDBInput
                                        value={formValues.signinurl}
                                        onChange={(e) => handleInputChange(e, "signinurl")}
                                        placeholder="https://login.microsoftonline.com/{tenant-id}/saml2"
                                        disabled={true}
                                    />
                                    {formErrors.signinurl && (
                                        <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
                                            {formErrors.signinurl}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="add-numbermain">
                                <div className="tel-numbers fw-bold">
                                    Certificate <span className="required-asterisk">*</span>
                                    <MDBIcon
                                        far
                                        icon="question-circle"
                                        className="ms-2 Certificate"
                                    />
                                </div>

                                {/* Change Certificate Button */}
                                <div>
                                    <MDBBtn className="px-4" onClick={handleCertificateClick}>
                                        Change Certificate
                                    </MDBBtn>

                                    {/* Expiry Date Text */}
                                    <div className="SSOCertificate mt-3">
                                        {formValues.ssoaccountid && formValues.expirydate && (() => {
                                            const isExpired =
                                                new Date(formValues.expirydate.replace(" ", "T")) < new Date();

                                            return (
                                                <span
                                                    id="certificatename"
                                                    style={{
                                                        color: isExpired ? "red" : "inherit",
                                                        fontWeight: isExpired ? "bold" : "normal",
                                                    }}
                                                >
                                                    {isExpired ? (
                                                        <>
                                                            Expired on <strong>{formValues.expirydate}</strong>
                                                        </>
                                                    ) : (
                                                        <>
                                                            Expires on <strong>{formValues.expirydate}</strong>
                                                        </>
                                                    )}
                                                </span>
                                            );
                                        })()}
                                    </div>

                                    {/* Certificate Error */}
                                    {formErrors.certificate && (
                                        <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
                                            {formErrors.certificate}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Radio Buttons */}
                            <div className="d-flex my-4">
                                <div className="w-30">
                                    <MDBRadio
                                        name="inlineRadio"
                                        id="inlineRadio1"
                                        value={0}
                                        label="Optional"
                                        inline
                                        checked={formValues.loginoption === 0}
                                        onChange={() => handleRadioChange(0)}
                                    />
                                    <div className="phrecording text-secondary ps-1">
                                        {t("MSOptional")}
                                    </div>
                                </div>
                                <div>
                                    <MDBRadio
                                        name="inlineRadio"
                                        id="inlineRadio2"
                                        value={1}
                                        label="Required"
                                        inline
                                        checked={formValues.loginoption === 1}
                                        onChange={() => handleRadioChange(1)}
                                    />
                                    <div className="phrecording text-secondary ps-1">
                                        {t("MSRequired")}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {(isEditedSso || isSsoToggleEdited) && (
                        <SaveChanges
                            onClose={resetForm}
                            isdirtyFields={true}
                            onSave={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleSubmit(submitSSOConfig)();
                                setEdited(false);
                                setEditedSso(false);
                                setIsSsoToggleEdited(false);
                            }}
                        />
                    )}
                </div>

                {/* Upload Certificate Modal */}
                <MDBModal
                    open={basicModal}
                    onClose={() => setBasicModal(false)}
                    tabIndex="-1"
                >
                    <MDBModalDialog size="lg">
                        <MDBModalContent>
                            <MDBModalHeader>
                                <MDBModalTitle>Upload the metadata file</MDBModalTitle>
                                <MDBBtn
                                    className="btn-close"
                                    color="none"
                                    onClick={() => setBasicModal(false)}
                                ></MDBBtn>
                            </MDBModalHeader>
                            <MDBModalBody>
                                <div className="uploadOuter mt-1 w-100">
                                    {/* Make the upload box click open our single hidden input */}
                                    <span
                                        className="dragBox2 upload-content"
                                        style={{ cursor: "pointer" }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleCertificateClick();
                                        }}
                                    >
                                        <img src={uploadIcon} className="upload-img" alt="Upload" />
                                        <span>Click to browse or drag and drop a file here.</span>
                                        {/* removed duplicate input from modal; clicking span triggers persistent input */}
                                    </span>
                                    <div className="add-numbermain">
                                        <span>
                                            <span style={{ color: "red" }}>* </span>
                                            Please upload the metadata file.
                                        </span>
                                    </div>
                                </div>
                            </MDBModalBody>
                        </MDBModalContent>
                    </MDBModalDialog>
                </MDBModal>

                {showSSODisableModal && (
                    <ConfirmAlertMS
                        showSSODisableModal={showSSODisableModal}
                        setShowSSODisableModal={setShowSSODisableModal}
                        messagehead={"Disable SSO"}
                        message={
                            "Are you sure you want to disable SSO? This action will remove the configuration."
                        }
                        onConfirm={async () => {
                            try {
                                await submitSSOConfig(1);
                                clearFormData();
                                setSsoEnable(false);
                                setShowSSODisableModal(false);
                                setEdited(false);
                                setEditedSso(false);
                                setIsSsoToggleEdited(false);

                                if (fileInputRef.current) {
                                    fileInputRef.current.value = '';
                                }
                            } catch (err) {
                                console.error("Failed to delete SSO config:", err);
                            }
                        }}
                        onCancel={() => {
                            setShowSSODisableModal(false);
                        }}
                    />
                )}

                <SSOInformationGuide
                    showModal={showInfoGuide}
                    setShowModal={setShowInfoGuide}
                />

                <TooltipMsSso expiryDate={formValues.expirydate} />

                {(isEditedSso || isSsoToggleEdited) && active && (
                    <SaveChanges
                        onSave={(e) => submitSSOConfig()}
                        onClose={resetForm}
                    />
                )}


                {updateSuccessMessage && (
                    <MDBAlert
                        open
                        className="alert-center"
                        color="success"
                        autohide
                        position="bottom-right"
                        delay={300000}
                    >
                        <i className="fas fa-check-circle me-3"></i>
                        <span className="alert-msg">{updateSuccessMessage}</span>
                    </MDBAlert>
                )}

                {updateFailureMessage && (
                    <MDBAlert
                        open
                        className="alert-center"
                        color="danger"
                        autohide
                        position="bottom-right"
                        delay={3000}
                    >
                        {typeof updateFailureMessage === "string"
                            ? updateFailureMessage
                            : "Failed to save Microsoft Entra ID Single Sign-On configuration."}
                    </MDBAlert>
                )}

                {fileUploadSuccess && (
                    <MDBAlert
                        open
                        className="alert-center"
                        color="success"
                        autohide
                        position="bottom-right"
                        delay={3000}
                    >
                        File uploaded successfully!
                    </MDBAlert>
                )}
                {fileUploadFailure && (
                    <MDBAlert
                        open
                        className="alert-center"
                        color="danger"
                        autohide
                        position="bottom-right"
                        delay={3000}
                    >
                        Failed to upload metadata file!
                    </MDBAlert>
                )}

            </>
            {loading && (
                <div className="loadingclass-popup">
                    <div className="popup-bg-loader">
                        <MDBBtn disabled>
                            <MDBSpinner size="sm" role="status" tag="span" className="me-2" />
                            Loading...
                        </MDBBtn>
                    </div>
                </div>
            )}
        </React.Fragment>
    );
}
export default SingleSignOnSettings;